import { motion } from "framer-motion";
import { useRef, useState, useEffect, useCallback } from "react";
import { EducationalMessage } from "@/types/api";
import DefaultContentContainer from "@/components/shared/DefaultContainer/DefaultContentContainer";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import LoaderButton from "@/components/form/LoaderButton";
import { useGameStore } from "@/state/gameStore";
import CashCtrlLogoCentCoin from "@/components/logos/CashCtrlLogoCentCoin";

type Props = {
    message: EducationalMessage;
    onClose: () => void;
};

const contentVariants = {
    initial: { opacity: 0, scale: 1.4 },
    animate: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
        opacity: 0,
        scale: 0.95,
        transition: { duration: 0.2, ease: "easeIn" },
    },
} as const;

function getScrollMask(canScrollUp: boolean, canScrollDown: boolean): string | undefined {
    if (canScrollUp && canScrollDown) {
        return "linear-gradient(to bottom, transparent, black 2rem, black calc(100% - 2rem), transparent)";
    }
    if (canScrollDown) {
        return "linear-gradient(to bottom, black calc(100% - 2rem), transparent)";
    }
    if (canScrollUp) {
        return "linear-gradient(to bottom, transparent, black 2rem)";
    }
    return undefined;
}

const EducationalMessageDisplay = ({ message, onClose }: Props) => {
    const queueTimerDuration = useGameStore((state) => state.queueTimerDuration);
    const isDebug = queueTimerDuration === 0;

    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollUp(el.scrollTop > 1);
        setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        updateScrollState();

        const observer = new ResizeObserver(updateScrollState);
        observer.observe(el);

        return () => observer.disconnect();
    }, [updateScrollState]);

    return (
        <div className="absolute size-full p-4 flex flex-col justify-end z-20 pb-10">
            <motion.div className="h-full pt-14" variants={contentVariants} initial="initial" animate="animate" exit="exit">
                <DefaultContentContainer className="relative p-4 pt-8 h-full flex flex-col">
                    <div className="absolute -top-[74px] -translate-x-1/2 left-1/2">
                        <CashCtrlLogoCentCoin />
                    </div>
                    <h1 className="text-white text-xl w-full text-center font-bold mb-2 leading-[1]">CASH CTRL Finanzinfos</h1>
                    {message.image && (
                        <StrapiMedia
                            size="md"
                            image={message.image}
                            className="object-cover rounded-lg aspect-[16/9]"
                            useImageAspectRatio={false}
                        />
                    )}
                    <h2 className="font-bold text-white/65 leading-tight mb-2 mt-3">{message.headline}</h2>
                    <div
                        ref={scrollRef}
                        onScroll={updateScrollState}
                        className="relative overflow-y-auto flex-1 min-h-0"
                        style={{ maskImage: getScrollMask(canScrollUp, canScrollDown) }}
                    >
                        <p className="leading-tight pb-6">{message.text}</p>
                    </div>
                    <LoaderButton onClick={onClose} className="shrink-0 mt-3" duration={isDebug ? 0 : undefined}>
                        Verstanden
                    </LoaderButton>
                </DefaultContentContainer>
            </motion.div>
        </div>
    );
};

export default EducationalMessageDisplay;